import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import {
  taskService,
  workflowService,
  taskColumnService,
  taskLabelService,
} from '../services/taskService';
import {
  TaskCreateDto,
  TaskUpdateDto,
  TaskFieldDefinitionDto,
  CreateTaskFieldDefinitionDto,
  WorkflowTypeDto,
  FormDefinitionDto,
} from '../types/django';
import { taskFieldService } from '../services/taskFieldService';


// =========================
// WORKFLOWS
// =========================
export const useWorkflows = () => {
  return useQuery({
    queryKey: ['workflows'],
    queryFn: () => workflowService.getAll(),
  });
};

export const useCreateWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      workflow_type: string;
      color?: string;
      is_active?: boolean;
    }) => workflowService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
};
export const useDeleteWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => workflowService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
};

// ===============
// WORKFLOW TYPES 
// ===============
export const useWorkflowTypes = (search?: string) => {
  return useQuery<WorkflowTypeDto[]>({
    queryKey: ['workflow-types', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await api.get('/workflow-types/', { params });
      
      return Array.isArray(response.data) 
        ? response.data 
        : response.data?.results || [];
    },
  });
};

export const useCreateWorkflowType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; code: string }) => {
      const response = await api.post('/workflow-types/', {
        name: data.name,
        code: data.code,
        est_archive: false
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-types'] });
    },
  });
};

export const useUpdateWorkflowType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { name: string; code?: string } }) => {
      const response = await api.patch(`/workflow-types/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-types'] });
    },
  });
};

export const useDeleteWorkflowType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.patch(`/workflow-types/${id}/`, { est_archive: true });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-types'] });
    },
  });
};

export const useRestoreWorkflowType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.patch(`/workflow-types/${id}/`, { est_archive: false });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-types'] });
    },
  });
};

// =========================
// FORMS DEFINITIONS (LISTE)
// =========================
export function useFormDefinitions(workflowTypeId?: number | null) {
  return useQuery<FormDefinitionDto[]>({
    queryKey: ['formDefinitions', workflowTypeId],

    queryFn: async (): Promise<FormDefinitionDto[]> => {
      const url = workflowTypeId 
        ? `/forms/?workflow_type=${workflowTypeId}` 
        : '/forms/';

      const response = await api.get(url);
      const rawData = response.data;

      const forms = Array.isArray(rawData)
        ? rawData
        : rawData?.results || [];

      return forms.map((form: any) => {
        if (!form.fields) return form;

        const uniqueFields = Object.values(
          form.fields.reduce((acc: any, f: any) => {
            acc[f.code] = f;
            return acc;
          }, {})
        );

        return {
          ...form,
          fields: uniqueFields,
        };
      });
    },

    enabled: true,

    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =========================
// UN GABARIT DE FORMULAIRE (DÉTAILS)
// =========================
export function useFormDefinition(id: number | string | null) {
  return useQuery({
    queryKey: ['forms', id],
    queryFn: async () => { 
      if (!id) return null;
      
      const formResponse = await api.get(`/forms/${id}/`);
      const formData = formResponse.data;

      const params = new URLSearchParams();
      params.append('form', String(id)); 

      const fieldsResponse = await api.get('/task-field-definitions/', { params });
      
      const fields = Array.isArray(fieldsResponse.data) 
        ? fieldsResponse.data 
        : fieldsResponse.data?.results || [];

      const sectionsMap: { [key: string]: any[] } = {};
      
      fields.forEach((field: any) => {
        let secTitle = 'Section 1';
        
        if (field.section) {
          secTitle = typeof field.section === 'object' ? (field.section.title || 'Section 1') : field.section;
        } else if (field.section_title) {
          secTitle = field.section_title;
        }

        if (!sectionsMap[secTitle]) {
          sectionsMap[secTitle] = [];
        }
        sectionsMap[secTitle].push(field);
      });

      const structuredSections = Object.keys(sectionsMap).map((title) => ({
        title: title,
        fields: sectionsMap[title].sort((a, b) => (a.position || 0) - (b.position || 0))
      }));

      return {
        id: formData.id,
        name: formData.name || '',
        description: formData.description || '',
        workflow: formData.workflow_type || '',
        formSections: structuredSections 
      };
    },
    enabled: id !== null && id !== undefined && id !== '',
  });
}

export const useCreateFormDefinition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { name: string; workflow_type: number; sections: any[] }) => {
     
      const formResponse = await api.post('/forms/', {
        name: payload.name,
        workflow_type: payload.workflow_type,
        position: 1,
        is_active: true
      });

      const createdFormId = formResponse.data.id;
      const fieldPromises: Promise<any>[] = [];

      payload.sections.forEach((sec) => {
        sec.fields.forEach((f: any) => {
          fieldPromises.push(
            api.post('/task-field-definitions/', {
              form: createdFormId,
              name: f.name,
              code: f.code,
              field_type: f.field_type,
              required: f.required,
              options: f.options, 
              section: sec.title,
              position: f.position,
              default_value: f.default_value,
              is_active: true
            }).then(res => res.data)
          );
        });
      });

      await Promise.all(fieldPromises);
      return formResponse.data;
    },
    onSuccess: (_, variables) => {
      const { workflow_type } = variables;
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      queryClient.invalidateQueries({ queryKey: ['formDefinitions'] });
      queryClient.invalidateQueries({ queryKey: ['task-field-definitions', workflow_type] });
    },
  });
};

export const useUpdateFormDefinition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { id: number; name: string; workflow_type: number; sections: any[] }) => {
      // 1. Mise à jour du conteneur
      await api.patch(`/forms/${payload.id}/`, {
        name: payload.name,
        workflow_type: payload.workflow_type,
      });

      // 2. Récupération des anciens champs
      const existingFieldsResponse = await api.get(`/task-field-definitions/?form=${payload.id}`);
      const existingFields = Array.isArray(existingFieldsResponse.data) 
        ? existingFieldsResponse.data 
        : existingFieldsResponse.data?.results || [];

      for (const field of existingFields) {
        await api.delete(`/task-field-definitions/${field.id}/`);
      }

      // 4. Recréation des champs propres
      const fieldPromises: Promise<any>[] = [];
      payload.sections.forEach((sec) => {
        sec.fields.forEach((f: any) => {
          fieldPromises.push(
            api.post('/task-field-definitions/', {
              form: payload.id, 
              name: f.name,
              code: f.code,
              field_type: f.field_type,
              required: f.required,
              options: f.options, 
              section: sec.title,
              position: f.position,
              default_value: f.default_value, 
              is_active: true
            }).then(res => res.data)
          );
        });
      });

      return Promise.all(fieldPromises);
    },
    onSuccess: (_, variables) => {
      const { workflow_type, id } = variables;

      queryClient.removeQueries({ queryKey: ['forms', id] });
      queryClient.removeQueries({ queryKey: ['formDefinitions'] });
      queryClient.removeQueries({ queryKey: ['task-field-definitions'] }); 
      queryClient.removeQueries({ queryKey: ['task-field-definitions', workflow_type] });
      queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
  });
};
// =========================
// COLUMNS
// =========================
export const useColumns = (workflowId?: number) => {
  return useQuery({
    queryKey: ['columns', workflowId],
    queryFn: () => taskColumnService.getAll(workflowId), 
    enabled: !!workflowId,
  });
};

export const useCreateColumn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { workflow: number; name: string; position: number }) => {
      return taskColumnService.create(payload); 
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['columns', variables.workflow] });
      queryClient.invalidateQueries({ queryKey: ['kanban', variables.workflow] });
    },
  });
};

export const useUpdateColumn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { id: number; name: string; workflowId: number }) => {
      return taskColumnService.update(payload.id, { name: payload.name });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['columns', variables.workflowId] });
      queryClient.invalidateQueries({ queryKey: ['kanban', variables.workflowId] });
    },
  });
};

export const useDeleteColumn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { id: number; workflowId: number }) => {
      return taskColumnService.delete(payload.id);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['columns', variables.workflowId] });
      queryClient.invalidateQueries({ queryKey: ['kanban', variables.workflowId] });
    },
  });
};

// =========================
// LABELS
// =========================
export const useLabels = () => {
  return useQuery({
    queryKey: ['labels'],
    queryFn: () => taskLabelService.getAll(),
  });
};

// =========================
// TASKS
// =========================
export const useTasks = (filters?: {
  workflow?: number;
  column?: number;
  all?: boolean;
}) => {
  return useQuery({
    queryKey: ['tasks', filters?.workflow, filters?.column, filters?.all],
    queryFn: () => taskService.getAll(filters),
  });
};

export const useTask = (id: number) => {
  return useQuery({
    queryKey: ['task', id],
    queryFn: () => taskService.getById(id),
    enabled: Boolean(id)
  });
};

// =========================
// TaskFields
// =========================
export const useTaskFields = (workflowId?: number) => {
  return useQuery<TaskFieldDefinitionDto[]>({
    queryKey: ['task-field-definitions', workflowId],
    queryFn: () => taskFieldService.getAll(workflowId),
    enabled: !!workflowId,
  });
};

export const useCreateTaskField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTaskFieldDefinitionDto) =>
      taskFieldService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['task-field-definitions'],
      });
    },
  });
};
  
// =========================
// CREATE TASK
// =========================
export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TaskCreateDto) => taskService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false });
    },
  });
};

// =========================
// UPDATE TASK
// =========================
export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaskUpdateDto }) =>
      taskService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', id] });
    },
  });
};

// =========================
// DELETE TASK
// =========================
export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => taskService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// =========================
// MOVE TASK (KANBAN)
// =========================
export const useMoveTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      columnId,
      position,
    }: {
      taskId: number;
      columnId: number;
      position?: number;
    }) => taskService.move(taskId, columnId, position),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};